import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRoute } from 'wouter'
import { createPortal } from 'react-dom'
import {
  LuCircleAlert,
  LuClipboardList,
  LuDownload,
  LuFileSpreadsheet,
  LuHistory,
  LuPencil,
  LuTrash2,
  LuUpload,
  LuX,
} from 'react-icons/lu'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

// ─── Constants & mapping logic ────────────────────────────────────────────────

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000'

const TIPUS_OPTIONS = [
  'OB - 1. OBRES',
  'SU - 3. SUBMINISTRAMENTS',
  'SE - 5. SERVEIS',
]

const TRAMITACIO_OPTIONS = [
  'O - 1.Ordinària',
  'U - 2.Urgent',
  'E - 5.Emergència',
]

// Còpia literal del desplegable del full 'Hidden' de la plantilla (columna K).
// No en canviïs el text: Excel valida la cel·la contra aquestes cadenes exactes.
const SUBMIN_OPTIONS = [
  'AB - Adq. o arrend de productes i béns',
  "AE - Adq. o arrend. d'equips i sistemes per tractament informació",
  'AD - Adquisició',
  "DP - Dispositius i programes contractats conjunt amb l'adquisició",
  'EB - Lliurament béns de forma succesiva i per preu unitari',
  'LL - Lloguer',
  "ME - Manteniment d'equips i sistemes per el tractament de la inf",
  'SE - Subministraments de fabricació',
]

const CETIS_OPTIONS = [
  "02 - Empresa d'inserció (EI)",
  "07 - Centre especial de treball d'iniciativa social (CETIS)",
  '06 - Cap de les anteriors',
]

const IVA_OPTIONS = ['0', '0.04', '0.10', '0.21']

const FIXED_A  = 'Ajuntament Santa Margarida i els Monjos'
const FIXED_E  = 1
const FIXED_G  = 'O - 1.Ordinària'
const FIXED_N  = 'ES - SPAIN (España)'
const FIXED_Q  = '06 - Cap de les anteriors'
const FIXED_R  = 'No'
// L'espec de Gestinet diu "Santa Margarida i els Monjos", però el desplegable de
// la plantilla només accepta províncies → els Monjos (Alt Penedès) = Barcelona.
const FIXED_AD = '08 - Barcelona'
const FIXED_AE = 'ES - SPAIN (España)'

function mapTipus(val) {
  if (!val) return ''
  const v = String(val).toLowerCase()
  if (v.includes('obra'))                              return 'OB - 1. OBRES'
  if (v.includes('suministro') || v.includes('subm')) return 'SU - 3. SUBMINISTRAMENTS'
  if (v.includes('servic'))                            return 'SE - 5. SERVEIS'
  return String(val)
}

const isSubmTipus = (mappedTipus) => String(mappedTipus || '').startsWith('SU')

const SUBMIN_CODES = new Set(SUBMIN_OPTIONS.map((o) => o.slice(0, 2).toUpperCase()))
const EXP_RE = /^\d+\/\d{4}$/          // '1685/2026'
const CPV_RE = /\d{8}-\d/              // '45310000-5' (també dins 'CPV 48218000-9')

// El camp 'Text lliure' porta 4 blocs separats per comes:
//   tipus de subministrament, descripció, codi CPV, expedient
// Es llegeix de dreta a esquerra perquè la descripció pot portar comes i el tipus
// sovint no hi és (contractes que no són subministraments, o subministraments
// on comptabilitat no l'ha posat). El tipus només s'accepta si és un codi vàlid
// del desplegable; si no, es deixa buit i la fila queda pendent de revisar.
function parseTextLliure(text, isSubm) {
  const parts = String(text ?? '').split(',').map((s) => s.trim())

  const expedient = parts.length > 1 && EXP_RE.test(parts[parts.length - 1]) ? parts.pop() : ''

  let cpv = ''
  if (parts.length > 1) {
    const m = parts[parts.length - 1].match(CPV_RE)
    if (m) { cpv = m[0]; parts.pop() }
  }

  let tipus = ''
  if (parts.length > 1 && SUBMIN_CODES.has(parts[0].toUpperCase())) {
    const code = parts.shift()
    if (isSubm) tipus = code
  }

  const desc = parts.filter((p) => p !== '').join(', ')
  return { tipus, desc, cpv, expedient }
}

// Població Tercer en text pla: MAJÚSCULES i sense el codi postal al davant si
// n'hi hagués. Ja no va a la plantilla (la columna O porta el codi INE), però es
// guarda a la BD com a `poblacio_tercer` per poder auditar d'on surt cada codi.
function toPoblacio(val) {
  if (val == null || val === '') return ''
  return String(val)
    .replace(/^\s*\d{4,5}[\s-]*/, '')   // treu un codi postal al davant ("08000 MADRID")
    .trim()
    .toUpperCase()
}

// Codi postal a 5 dígits. Si l'Excel el porta com a número, el 08730 hi arriba
// com a 8730 i cal reconstruir el zero del davant.
function toCp(val) {
  if (val == null || val === '') return ''
  const d = String(val).replace(/\D/g, '')
  if (!d || d.length > 5) return ''
  return d.padStart(5, '0')
}

// ─── Catàleg CP → codi INE de municipi ───────────────────────────────────────
// La plantilla vol el codi INE a la columna O, però el SicalWin només porta el
// codi postal. El catàleg viu a la BD (taula `cp_ine`) i el descarreguem un cop
// per sessió: una importació resol milers de files i no pot fer una crida cada
// vegada.
let cpIneCache = null
function carregaCpIne() {
  if (!cpIneCache) {
    cpIneCache = fetch(`${API}/contractes/cp-ine`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .catch((err) => { cpIneCache = null; throw err })   // no cachegis un error
  }
  return cpIneCache
}

/**
 * Resol el codi INE d'un contracte a partir del seu codi postal.
 * Torna '' si no es pot resoldre amb certesa, i llavors la fila queda marcada
 * com a incompleta perquè es completi a mà: val més una casella buida que un
 * municipi equivocat.
 */
function resolCodiIne(cp, poblacio, mapa) {
  if (!cp || !mapa) return ''
  const candidats = mapa[cp]
  if (!candidats || candidats.length === 0) return ''
  if (candidats.length === 1) return candidats[0][0]

  // CP compartit entre municipis: desempatem amb el nom de la Població Tercer.
  const norm = (s) => String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]/g, '')
  const objectiu = norm(poblacio)
  if (objectiu) {
    const encert = candidats.filter(([, nom]) => norm(nom) === objectiu)
    if (encert.length === 1) return encert[0][0]
  }
  return ''   // segueix ambigu → a revisar
}

// El text lliure porta el tipus de subministrament com a codi curt ('SE', 'LL'…).
// El convertim al text complet que exigeix el desplegable de la plantilla.
// Si el codi no existeix (p. ex. el 'SU' erroni), tornem '' perquè la fila surti
// marcada com a pendent de revisar en comptes d'escriure un valor invàlid.
function mapSubministrament(val) {
  if (val == null || val === '') return ''
  const code = String(val).trim().toUpperCase()
  return SUBMIN_OPTIONS.find(o => o.slice(0, 2).toUpperCase() === code) || ''
}

function parseExcelDate(val) {
  if (val == null || val === '') return { year: '', full: '' }
  // xlsx with cellDates:true gives JS Date
  if (val instanceof Date) {
    const d = val
    const dd   = String(d.getDate()).padStart(2, '0')
    const mm   = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = String(d.getFullYear())
    return { year: yyyy, full: `${dd}/${mm}/${yyyy}` }
  }
  // String format DD/MM/YYYY
  if (typeof val === 'string') {
    const parts = val.trim().split('/')
    if (parts.length === 3) {
      return { year: parts[2].trim(), full: val.trim() }
    }
    const d = new Date(val)
    if (!isNaN(d)) {
      const dd   = String(d.getDate()).padStart(2, '0')
      const mm   = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = String(d.getFullYear())
      return { year: yyyy, full: `${dd}/${mm}/${yyyy}` }
    }
  }
  return { year: '', full: String(val) }
}

function parseAmount(val) {
  if (val == null || val === '') return null
  if (typeof val === 'number') return val
  // Handle Spanish format: 2.388,21 or 2388,21
  const s = String(val).trim()
  // If has comma and dot: assume dot=thousands, comma=decimal
  if (s.includes(',') && s.includes('.')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || null
  }
  // Only comma: decimal separator
  if (s.includes(',')) {
    return parseFloat(s.replace(',', '.')) || null
  }
  return parseFloat(s) || null
}

// Arrodoneix a 2 decimals (imports). Preserva '' i valors no numèrics.
const round2 = (v) => {
  if (v == null || v === '') return v
  const n = Number(v)
  return isNaN(n) ? v : Math.round(n * 100) / 100
}

// Col B origen → Expedient: descarta el primer número i agafa la resta.
// '2025       920 22200' → '920 22200'
function extractExpedient(val) {
  if (val == null || val === '') return ''
  const s = String(val).trim()
  const rest = s.replace(/^\S+\s+/, '')   // treu el primer token + els espais
  return rest === s ? s : rest.trim()      // si no hi ha espai, deixa'l tal qual
}

function fmtEur(n) {
  if (n == null || n === '') return '—'
  return Number(n).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })
}

function buildEmptyRow(idx) {
  return {
    _id: idx,
    colA:  FIXED_A,
    colB:  '',
    colC:  '',
    colD:  '',
    colE:  FIXED_E,
    colF:  '',
    colG:  FIXED_G,
    colH:  '',
    colI:  '',
    colJ:  '',
    colK:  '',
    colL:  '',
    colM:  '',
    colN:  FIXED_N,
    colO:  '',
    colP:  '',
    colQ:  FIXED_Q,
    colR:  FIXED_R,
    colS:  '',
    colT:  '',
    colU:  '', colV: '', colW: '', colX: '', colY: '', colZ: '',
    colAA: '', colAB: '', colAC: '',
    colAD: FIXED_AD,
    colAE: FIXED_AE,
    colAF: '', colAG: '',
  }
}

// Localitza la fila de capçalera del fitxer SicalWin (conté "Import" i "Expedient").
// Si no la troba, assumeix que és la primera fila amb contingut.
function findHeaderRow(raw) {
  const isHeader = (r) => {
    if (!Array.isArray(r)) return false
    const txt = r.map(c => String(c ?? '').toLowerCase()).join('|')
    return txt.includes('import') && txt.includes('expedient')
  }
  const idx = raw.findIndex(isHeader)
  if (idx !== -1) return idx
  const firstFilled = raw.findIndex(r => Array.isArray(r) && r.some(c => c != null && c !== ''))
  return firstFilled === -1 ? 0 : firstFilled
}

// Format del fitxer SicalWin:
//   A = Import (líquid) · B = Expedient · C = Exercici · D = tercer (NIF)
//   E = Nom.terc. · F = Text lliure · G = Data doc. · H = Població Tercer
//   I = Tipus Contracto · J = Codi Postal
function mapSheetRowToOutput(rawRow, idx, mapaCpIne) {
  // rawRow: array indexed by column (0=A, 1=B, 2=C, ...)
  const date   = parseExcelDate(rawRow[6])   // Col G  → data doc.
  const amount = parseAmount(rawRow[0])       // Col A  → import líquid
  const amountSenseIva = amount != null ? round2(amount / 1.21) : ''
  const amountAmbIva   = amount != null ? amount : ''
  const tipus = mapTipus(rawRow[8])                    // Col I (tipus contracte)
  const tl    = parseTextLliure(rawRow[5], isSubmTipus(tipus))  // Col F

  // El municipi de la plantilla (col. O) és el codi INE, i el SicalWin només
  // porta el codi postal (col. J). La població (col. H) només s'usa per
  // desempatar els CP compartits entre municipis.
  const cp       = toCp(rawRow[9])
  const poblacio = toPoblacio(rawRow[7])

  return {
    _id:   idx,
    _cp:       cp,
    _poblacio: poblacio,
    colA:  FIXED_A,
    colB:  tipus,
    colC:  rawRow[2] != null && rawRow[2] !== ''        // Col C (exercici)
             ? String(rawRow[2]).trim()
             : date.year,                               // fallback: any de la data doc.
    colD:  tl.expedient || extractExpedient(rawRow[1]), // Text lliure (4a pos.); fallback col B
    colE:  FIXED_E,
    colF:  tl.desc,                                     // Text lliure → descripció
    colG:  FIXED_G,
    colH:  amountSenseIva,                              // Col A / 1.21
    colI:  amountAmbIva,                                // Col A (amb IVA)
    colJ:  amountSenseIva,                              // Valor estimat = pressupost sense IVA
    colK:  mapSubministrament(tl.tipus),               // Text lliure → tipus subm.
    colL:  rawRow[3] != null ? String(rawRow[3]) : '',  // Col D (NIF)
    colM:  rawRow[4] != null ? String(rawRow[4]) : '',  // Col E (Nom)
    colN:  FIXED_N,
    colO:  resolCodiIne(cp, poblacio, mapaCpIne),      // Col J (CP) → codi INE
    colP: '',
    colQ:  FIXED_Q,
    colR:  FIXED_R,
    colS:  tl.cpv,                                      // Text lliure → codi CPV
    colT:  date.full,                                   // Data adjudicació (col G)
    colU: '',
    colV:  date.full,                                  // Data inici execució = data adjudicació
    colW:  date.full,                                  // Data fi execució = data adjudicació
    colX: '', colY: '', colZ: '',
    colAA: amountSenseIva,                             // Import adj. sense IVA = H
    colAB: amountAmbIva,                               // Import adj. amb IVA = I
    colAC: '',
    colAD: FIXED_AD,
    colAE: FIXED_AE,
    colAF: '', colAG: '',
  }
}

// Agrupa les files que comparteixen expedient: els imports se sumen i la resta
// de camps es prenen de la primera aparició (segons l'espec de Gestinet, han de
// ser iguals). Les files sense expedient no s'agrupen mai entre elles.
function mergeByExpedient(rows) {
  const byExp = new Map()
  const out   = []

  for (const row of rows) {
    const key = String(row.colD ?? '').trim().toUpperCase()
    if (!key) { out.push(row); continue }

    const prev = byExp.get(key)
    if (!prev) {
      byExp.set(key, row)
      out.push(row)
      continue
    }
    // Suma sobre el pressupost amb IVA i recalcula sense IVA i valor estimat.
    const ambIva = round2((Number(prev.colI) || 0) + (Number(row.colI) || 0))
    prev.colI = ambIva
    prev.colH = round2(ambIva / 1.21)
    prev.colJ = prev.colH
    prev._merged = (prev._merged || 1) + 1
  }

  // Reindexa perquè _id segueixi sent la posició real a la taula.
  return out.map((r, i) => ({ ...r, _id: i }))
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const INPUT_CLS  = 'w-full border border-[#c8e6da] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#006F53]'
const LABEL_CLS  = 'block text-xs font-medium text-gray-500 mb-1'
const SELECT_CLS = INPUT_CLS

// ─── Edit Modal ───────────────────────────────────────────────────────────────

function Sel({ label, col, k, opts, form, set }) {
  return (
    <div>
      <label className={LABEL_CLS}>{col} — {label}</label>
      <select className={SELECT_CLS} value={form[k] || ''} onChange={e => set(k, e.target.value)}>
        <option value="">— Selecciona —</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}
Sel.propTypes = { label: PropTypes.string, col: PropTypes.string, k: PropTypes.string, opts: PropTypes.array, form: PropTypes.object, set: PropTypes.func }

function Inp({ label, col, k, form, set, type = 'text', placeholder = '', money = false }) {
  const isNum = money || type === 'number'
  return (
    <div>
      <label className={LABEL_CLS}>{col} — {label}</label>
      <input
        type={isNum ? 'number' : type}
        step={money ? '0.01' : type === 'number' ? '1' : undefined}
        inputMode={money ? 'decimal' : undefined}
        className={INPUT_CLS} value={form[k] ?? ''} placeholder={placeholder}
        onChange={e => set(k, isNum && e.target.value !== '' ? parseFloat(e.target.value) : e.target.value)}
        onBlur={money ? () => set(k, round2(form[k])) : undefined} />
    </div>
  )
}
Inp.propTypes = { label: PropTypes.string, col: PropTypes.string, k: PropTypes.string, form: PropTypes.object, set: PropTypes.func, type: PropTypes.string, placeholder: PropTypes.string, money: PropTypes.bool }

function SelSearch({ label, col, k, listId, opts, form, set }) {
  return (
    <div>
      <label className={LABEL_CLS}>{col} — {label}</label>
      <input list={listId} className={INPUT_CLS} value={form[k] || ''}
        onChange={e => set(k, e.target.value)} placeholder="Escriu per cercar..." />
      <datalist id={listId}>
        {opts.map(o => <option key={o} value={o} />)}
      </datalist>
    </div>
  )
}
SelSearch.propTypes = { label: PropTypes.string, col: PropTypes.string, k: PropTypes.string, listId: PropTypes.string, opts: PropTypes.array, form: PropTypes.object, set: PropTypes.func }

function EditModal({ row, opts, onSave, onClose }) {
  const [form, setForm] = useState({ ...row })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  function handleAmountChange(k, v) {
    const n = parseFloat(String(v).replace(',', '.'))
    set(k, isNaN(n) ? v : n)
    // Imports encadenats: pressupost sense IVA (H) ↔ amb IVA (I), valor estimat (J = H)
    // i imports d'adjudicació (AA = H, AB = I), que la plataforma exigeix iguals.
    if (k === 'colI') {
      const amb   = isNaN(n) ? '' : n
      const sense = amb !== '' ? round2(amb / 1.21) : ''
      set('colH', sense); set('colJ', sense); set('colAA', sense); set('colAB', amb)
    }
    if (k === 'colH') {
      const sense = isNaN(n) ? '' : n
      const amb   = sense !== '' ? round2(sense * 1.21) : ''
      set('colI', amb); set('colJ', sense); set('colAA', sense); set('colAB', amb)
    }
  }

  // Data adjudicació (T) → copia a data inici (V) i data fi (W) d'execució, que
  // la plataforma exigeix omplertes amb el mateix valor.
  function handleDataAdjChange(v) {
    set('colT', v); set('colV', v); set('colW', v)
  }

  // En sortir d'un camp d'import, deixa'l net a 2 decimals.
  const blurRound = (k) => set(k, round2(form[k]))

  const sec = (title) => (
    <h3 className="col-span-2 text-xs font-semibold text-[#006F53] uppercase tracking-wider mt-4 border-b border-[#e0f0e8] pb-1">
      {title}
    </h3>
  )

  const paisos   = opts.N  || []
  const paisosAE = opts.AE || []
  const provsAD  = opts.AD || []

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">

        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-[#e0f0e8]">
          <h2 className="text-base font-semibold text-[#006F53]">
            Editar fila — {form.colM || `Fila ${row._id + 1}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><LuX size={20} /></button>
        </div>

        <div className="px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-3">

          {sec('Informació general')}
          <div className="col-span-2">
            <label className={LABEL_CLS}>A — Entitat</label>
            <input className={INPUT_CLS} value={form.colA || ''} onChange={e => set('colA', e.target.value)} />
          </div>
          <Sel  label="Tipus de contracte"  col="B" k="colB" opts={TIPUS_OPTIONS}      form={form} set={set} />
          <Inp  label="Any d'execució"       col="C" k="colC" form={form} set={set} placeholder="2025" />
          <Inp  label="Expedient"            col="D" k="colD" form={form} set={set} />
          <Inp  label="Número de lot"        col="E" k="colE" form={form} set={set} type="number" />
          <div className="col-span-2">
            <label className={LABEL_CLS}>F — Descripció del contracte</label>
            <textarea rows={2} className={INPUT_CLS} value={form.colF || ''} onChange={e => set('colF', e.target.value)} />
          </div>
          <Sel  label="Tipus de tramitació"  col="G" k="colG" opts={TRAMITACIO_OPTIONS} form={form} set={set} />

          {sec('Imports (€)')}
          <p className="col-span-2 -mt-1 text-xs text-gray-400">
            Escriu qualsevol dels imports i la resta es calcula sola (IVA 21 %). Valor estimat = pressupost sense IVA. Tot a 2 decimals.
          </p>
          <div>
            <label className={LABEL_CLS}>H — Pressupost licitació sense IVA</label>
            <input type="number" step="0.01" inputMode="decimal" className={INPUT_CLS} value={form.colH ?? ''}
              onChange={e => handleAmountChange('colH', e.target.value)} onBlur={() => blurRound('colH')} />
          </div>
          <div>
            <label className={LABEL_CLS}>I — Pressupost licitació amb IVA</label>
            <input type="number" step="0.01" inputMode="decimal" className={INPUT_CLS} value={form.colI ?? ''}
              onChange={e => handleAmountChange('colI', e.target.value)} onBlur={() => blurRound('colI')} />
          </div>
          <div>
            <label className={LABEL_CLS}>J — Valor estimat</label>
            <input type="number" step="0.01" inputMode="decimal" className={INPUT_CLS} value={form.colJ ?? ''}
              onChange={e => set('colJ', e.target.value === '' ? '' : parseFloat(e.target.value))} onBlur={() => blurRound('colJ')} />
          </div>
          <div>
            <label className={LABEL_CLS}>AC — Tipus IVA</label>
            <select className={SELECT_CLS} value={form.colAC || ''} onChange={e => set('colAC', e.target.value)}>
              <option value="">— Selecciona —</option>
              {IVA_OPTIONS.map(o => <option key={o} value={o}>{(parseFloat(o)*100).toFixed(0)}%</option>)}
            </select>
          </div>

          {sec('Proveïdor adjudicatari')}
          <Inp  label="NIF / CIF"            col="L" k="colL" form={form} set={set} placeholder="B87539284" />
          <Inp  label="Nom / Raó social"     col="M" k="colM" form={form} set={set} />
          {paisos.length > 0
            ? <SelSearch label="País adjudicatari" col="N" k="colN" listId="list-pais-adj" opts={paisos} form={form} set={set} />
            : <Inp label="País adjudicatari" col="N" k="colN" form={form} set={set} />
          }
          <Inp  label="Codi INE municipi" col="O" k="colO" form={form} set={set} />
          <Inp  label="PIME"                  col="P" k="colP" form={form} set={set} />
          <Sel  label="CETIS / EI"            col="Q" k="colQ" opts={CETIS_OPTIONS}    form={form} set={set} />
          <Inp  label="Reserva social"        col="R" k="colR" form={form} set={set} />

          {sec('Codi CPV')}
          <Inp  label="Codi CPV"             col="S" k="colS" form={form} set={set} placeholder="64211100-9" />
          <Sel  label="Tipus subministraments" col="K" k="colK" opts={SUBMIN_OPTIONS}  form={form} set={set} />

          {sec('Dates')}
          <div>
            <label className={LABEL_CLS}>T — Data adjudicació (DD/MM/AAAA)</label>
            <input className={INPUT_CLS} value={form.colT || ''} placeholder="03/07/2025"
              onChange={e => handleDataAdjChange(e.target.value)} />
          </div>
          <Inp  label="Data formalització (DD/MM/AAAA)" col="U" k="colU" form={form} set={set} placeholder="03/07/2025" />
          <Inp  label="Data inici execució (DD/MM/AAAA)" col="V" k="colV" form={form} set={set} placeholder="03/07/2025" />
          <Inp  label="Data fi execució (DD/MM/AAAA)"  col="W" k="colW" form={form} set={set} placeholder="03/07/2025" />
          <Inp  label="Data liquidació (DD/MM/AAAA)"   col="AF" k="colAF" form={form} set={set} placeholder="03/07/2025" />

          {sec('Terminis')}
          <Inp  label="Termini: Anys"  col="X" k="colX" form={form} set={set} type="number" />
          <Inp  label="Termini: Mesos" col="Y" k="colY" form={form} set={set} type="number" />
          <Inp  label="Termini: Dies"  col="Z" k="colZ" form={form} set={set} type="number" />

          {sec("Imports d'adjudicació")}
          <Inp  label="Import adjudicació sense IVA" col="AA" k="colAA" form={form} set={set} money />
          <Inp  label="Import adjudicació amb IVA"   col="AB" k="colAB" form={form} set={set} money />
          <Inp  label="Import liquidació sense IVA"  col="AG" k="colAG" form={form} set={set} money />

          {sec('Lloc execució')}
          {provsAD.length > 0
            ? <SelSearch label="Lloc execució (província)" col="AD" k="colAD" listId="list-prov-exec" opts={provsAD} form={form} set={set} />
            : <Inp label="Lloc execució" col="AD" k="colAD" form={form} set={set} />
          }
          {paisosAE.length > 0
            ? <SelSearch label="País origen del producte" col="AE" k="colAE" listId="list-pais-orig" opts={paisosAE} form={form} set={set} />
            : <Inp label="País origen del producte" col="AE" k="colAE" form={form} set={set} />
          }

        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#e0f0e8] flex justify-end gap-3 px-6 py-4">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel·lar
          </button>
          <button onClick={() => { onSave(form); onClose() }}
            className="px-5 py-2 text-sm bg-[#006F53] text-white rounded-lg hover:bg-[#005540]">
            Desar canvis
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

EditModal.propTypes = {
  row:     PropTypes.object.isRequired,
  opts:    PropTypes.object.isRequired,
  onSave:  PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

// ─── Upload Zone ──────────────────────────────────────────────────────────────

function UploadZone({ onParsed }) {
  const [dragging, setDragging]   = useState(false)
  const [loading, setLoading]     = useState(false)
  const fileRef                   = useRef()

  const processFile = useCallback(async (file) => {
    if (!file) return
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Cal seleccionar un fitxer Excel (.xlsx o .xls)')
      return
    }
    setLoading(true)
    try {
      const buffer = await file.arrayBuffer()
      const wb     = XLSX.read(buffer, { type: 'array', cellDates: true })
      const ws     = wb.Sheets[wb.SheetNames[0]]
      const raw    = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })

      // La capçalera no sempre és la primera fila (l'export de SicalWin la posa
      // a la fila 3). La localitzem pel text i comencem a llegir just després.
      const headerIdx = findHeaderRow(raw)
      const dataRows  = raw.slice(headerIdx + 1).filter(r => r.some(c => c != null && c !== ''))
      if (dataRows.length === 0) {
        toast.error("No s'han trobat dades al fitxer (no hi ha files sota la capçalera)")
        return
      }

      // Catàleg CP → INE per omplir la columna O. Si no es pot descarregar,
      // seguim endavant amb la columna buida: les files quedaran marcades com a
      // incompletes i es podran completar a mà, que és millor que avortar tota
      // la importació.
      let mapaCpIne = null
      try {
        mapaCpIne = await carregaCpIne()
      } catch (err) {
        toast.warn('No s\'ha pogut carregar el catàleg de codis postals: el codi INE del municipi s\'haurà d\'omplir a mà')
      }

      const mapped = dataRows.map((r, i) => mapSheetRowToOutput(r, i, mapaCpIne))
      const rows   = mergeByExpedient(mapped)
      const fusionades = mapped.length - rows.length

      toast.success(`${rows.length} fila${rows.length > 1 ? 'es' : ''} importada${rows.length > 1 ? 'es' : ''} correctament`)
      // Subministraments amb un codi de tipus que no és al desplegable de la plantilla.
      const senseTipus = rows.filter(r => isSubmTipus(r.colB) && !r.colK).length
      if (senseTipus > 0) {
        toast.warn(`${senseTipus} fila${senseTipus > 1 ? 'es' : ''} amb un tipus de subministrament no vàlid — cal triar-lo a mà abans de descarregar`)
      }
      if (fusionades > 0) {
        toast.info(`${fusionades} fila${fusionades > 1 ? 'es' : ''} amb expedient repetit ${fusionades > 1 ? 's\'han' : 's\'ha'} sumat a la seva`)
      }
      // CP absent, desconegut o compartit entre municipis: no s'ha resolt sol.
      if (mapaCpIne) {
        const senseIne = rows.filter(r => !r.colO).length
        if (senseIne > 0) {
          toast.warn(`${senseIne} fila${senseIne > 1 ? 'es' : ''} sense codi INE de municipi (codi postal buit, desconegut o compartit) — cal completar-${senseIne > 1 ? 'les' : 'la'} a mà`)
        }
      }
      onParsed(rows, file.name)
    } catch (err) {
      toast.error('Error llegint el fitxer: ' + err.message)
    } finally {
      setLoading(false)
    }
  }, [onParsed])

  const onDrop = useCallback((e) => {
    e.preventDefault()
    setDragging(false)
    processFile(e.dataTransfer.files[0])
  }, [processFile])

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => fileRef.current.click()}
      className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
        dragging ? 'border-[#006F53] bg-[#e8f5f0]' : 'border-[#006F53]/30 bg-[#f0faf6] hover:border-[#006F53]/60'
      }`}
    >
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={e => { processFile(e.target.files[0]); e.target.value = '' }}
      />
      <LuFileSpreadsheet size={40} className="mx-auto mb-3 text-[#006F53]" />
      {loading ? (
        <p className="text-sm text-gray-600">Processant fitxer...</p>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-700">Arrossega aquí l&apos;Excel de SicalWin</p>
          <p className="text-xs text-gray-400 mt-1">o fes clic per seleccionar el fitxer (.xlsx / .xls)</p>
          <p className="text-xs text-gray-400 mt-1">S&apos;importarà la primera pestanya · La capçalera es detecta automàticament</p>
        </>
      )}
    </div>
  )
}

UploadZone.propTypes = {
  onParsed: PropTypes.func.isRequired,
}

// ─── Data Table ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 25

// All 33 template columns in order (A=0 … AG=32)
const COLS = [
  { key: 'colA',  letter: 'A',  label: 'Organisme',                    w: 180, num: false },
  { key: 'colB',  letter: 'B',  label: 'Tipus contracte',              w: 90,  num: false },
  { key: 'colC',  letter: 'C',  label: "Any d'execució",               w: 60,  num: false },
  { key: 'colD',  letter: 'D',  label: 'Expedient',                    w: 90,  num: false },
  { key: 'colE',  letter: 'E',  label: 'Núm. lot',                     w: 55,  num: true  },
  { key: 'colF',  letter: 'F',  label: 'Descripció',                   w: 200, num: false },
  { key: 'colG',  letter: 'G',  label: 'Tramitació',                   w: 90,  num: false },
  { key: 'colH',  letter: 'H',  label: 'Pressupost s/IVA',             w: 100, num: true  },
  { key: 'colI',  letter: 'I',  label: 'Pressupost c/IVA',             w: 100, num: true  },
  { key: 'colJ',  letter: 'J',  label: 'Valor estimat',                w: 100, num: true  },
  { key: 'colK',  letter: 'K',  label: 'Tipus submin.',                w: 80,  num: false },
  { key: 'colL',  letter: 'L',  label: 'NIF',                          w: 90,  num: false },
  { key: 'colM',  letter: 'M',  label: 'Proveïdor',                    w: 180, num: false },
  { key: 'colN',  letter: 'N',  label: 'País adj.',                    w: 90,  num: false },
  { key: 'colO',  letter: 'O',  label: 'Codi INE municipi',           w: 110, num: false },
  { key: 'colP',  letter: 'P',  label: 'PIME',                         w: 60,  num: false },
  { key: 'colQ',  letter: 'Q',  label: 'CETIS/EI',                     w: 80,  num: false },
  { key: 'colR',  letter: 'R',  label: 'Reserva social',               w: 80,  num: false },
  { key: 'colS',  letter: 'S',  label: 'CPV',                          w: 90,  num: false },
  { key: 'colT',  letter: 'T',  label: 'Data adjudicació',             w: 95,  num: false },
  { key: 'colU',  letter: 'U',  label: 'Data formalització',           w: 95,  num: false },
  { key: 'colV',  letter: 'V',  label: 'Data inici exec.',             w: 95,  num: false },
  { key: 'colW',  letter: 'W',  label: 'Data fi exec.',                w: 95,  num: false },
  { key: 'colX',  letter: 'X',  label: 'Termini anys',                 w: 60,  num: true  },
  { key: 'colY',  letter: 'Y',  label: 'Termini mesos',                w: 60,  num: true  },
  { key: 'colZ',  letter: 'Z',  label: 'Termini dies',                 w: 60,  num: true  },
  { key: 'colAA', letter: 'AA', label: 'Import adj. s/IVA',            w: 100, num: true  },
  { key: 'colAB', letter: 'AB', label: 'Import adj. c/IVA',            w: 100, num: true  },
  { key: 'colAC', letter: 'AC', label: 'Tipus IVA',                    w: 60,  num: false },
  { key: 'colAD', letter: 'AD', label: 'Lloc exec.',                   w: 110, num: false },
  { key: 'colAE', letter: 'AE', label: 'País origen',                  w: 90,  num: false },
  { key: 'colAF', letter: 'AF', label: 'Data liquidació',              w: 95,  num: false },
  { key: 'colAG', letter: 'AG', label: 'Import liquidació s/IVA',      w: 100, num: true  },
]

// La columna O (codi INE del municipi) hi entra perquè, quan el codi postal no
// s'ha pogut resoldre, la fila ha de sortir marcada en comptes de baixar-se amb
// el municipi buit.
const REQUIRED = new Set(['colB', 'colL', 'colM', 'colH', 'colO'])

// Una fila és incompleta si li falta algun camp obligatori. Font única de veritat
// per als avisos de la taula, les targetes de resum i el pas de descàrrega.
// El tipus de subministrament (K) només és obligatori en contractes SU; en obres
// i serveis va buit i és correcte.
const isIncomplete = (r) =>
  [...REQUIRED].some(k => r[k] === '' || r[k] == null) ||
  (isSubmTipus(r.colB) && !r.colK)                   // subministrament sense tipus

function fmtCell(col, val) {
  if (val === null || val === undefined || val === '') return ''
  if (col.num && typeof val === 'number') return val.toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return String(val)
}

function DataTable({ rows, onEdit, onDelete }) {
  const [page, setPage] = useState(0)

  const warnings     = rows.filter(isIncomplete)
  const totalPages   = Math.ceil(rows.length / PAGE_SIZE)
  const pageRows     = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const globalOffset = page * PAGE_SIZE

  useEffect(() => { setPage(0) }, [rows.length])

  const thCls = 'px-3 py-2 text-center font-semibold border-r border-[#005540] whitespace-nowrap select-none'
  const tdCls = (col, val) => {
    const missing = REQUIRED.has(col.key) && (val === '' || val == null)
    return [
      'px-3 py-2 border-r border-[#e0f0e8] text-sm whitespace-nowrap overflow-hidden',
      col.num ? 'text-right font-mono' : 'text-left',
      missing ? 'bg-red-50 text-red-500' : '',
    ].join(' ')
  }

  return (
    <div>
      {warnings.length > 0 && (
        <div className="flex items-start gap-2 mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          <LuCircleAlert size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            <strong>{warnings.length}</strong> fila{warnings.length > 1 ? 'es' : ''} amb camps obligatoris buits (marcats en vermell).
          </span>
        </div>
      )}

      {/* Excel-style spreadsheet */}
      <div className="border border-[#c8e6da] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              {/* Row number col */}
              <col style={{ width: 48 }} />
              {COLS.map(c => <col key={c.key} style={{ width: c.w * 1.4 }} />)}
              {/* Actions col */}
              <col style={{ width: 72 }} />
            </colgroup>
            <thead className="sticky top-0 z-10">
              {/* Letter row */}
              <tr className="bg-[#004d3a] text-white text-xs">
                <th className={thCls + ' bg-[#003d2d]'}></th>
                {COLS.map(c => (
                  <th key={c.key} className={thCls}>{c.letter}</th>
                ))}
                <th className={thCls}></th>
              </tr>
              {/* Label row */}
              <tr className="bg-[#006F53] text-white text-xs">
                <th className="px-2 py-2 text-center border-r border-[#005540] text-gray-300">#</th>
                {COLS.map(c => (
                  <th key={c.key}
                    className="px-3 py-2 text-left font-medium border-r border-[#005540] truncate"
                    title={c.label}>
                    {c.label}
                  </th>
                ))}
                <th className="px-2 py-2 border-r border-[#005540]"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, i) => {
                const hasIssue = isIncomplete(row)
                const globalI  = globalOffset + i
                const rowBg    = hasIssue ? 'bg-yellow-50/70' : globalI % 2 === 0 ? 'bg-white' : 'bg-[#f7fdfb]'
                return (
                  <tr key={row._id}
                    className={`${rowBg} hover:bg-[#eaf6f1] transition-colors border-b border-[#e8f0ec]`}>
                    {/* Row number */}
                    <td className="px-2 py-2 text-center text-xs text-gray-400 bg-gray-50 border-r border-[#d8ece4] font-mono select-none">
                      {globalI + 1}
                    </td>
                    {/* Data cells */}
                    {COLS.map(col => {
                      const val = row[col.key]
                      // A l'expedient hi marquem les files que agrupen diverses
                      // línies d'origen (imports ja sumats).
                      const merged = col.key === 'colD' && row._merged > 1
                      return (
                        <td key={col.key}
                          className={tdCls(col, val)}
                          title={merged
                            ? `${val} — suma de ${row._merged} línies del fitxer d'origen`
                            : (val != null && val !== '' ? String(val) : undefined)}
                          style={{ maxWidth: col.w }}>
                          {fmtCell(col, val)}
                          {merged && (
                            <span className="ml-1 px-1 rounded bg-[#d8f0e6] text-[#006F53] text-[10px] font-semibold align-middle">
                              ×{row._merged}
                            </span>
                          )}
                        </td>
                      )
                    })}
                    {/* Actions */}
                    <td className="px-1 py-2 text-center border-r border-[#e0f0e8]">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => onEdit(row)}
                          className="p-1.5 rounded text-[#006F53] hover:bg-[#d8f0e6]" title="Editar">
                          <LuPencil size={14} />
                        </button>
                        <button onClick={() => onDelete(row._id)}
                          className="p-1.5 rounded text-red-400 hover:bg-red-50" title="Eliminar">
                          <LuTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <span>
            Mostrant {globalOffset + 1}–{Math.min(globalOffset + PAGE_SIZE, rows.length)} de {rows.length} files
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(0)} disabled={page === 0}
              className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
              className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">‹</button>
            {Array.from({ length: totalPages }, (_, idx) => idx)
              .filter(idx => Math.abs(idx - page) <= 2)
              .map(idx => (
                <button key={idx} onClick={() => setPage(idx)}
                  className={`px-2.5 py-1 rounded border transition-colors ${idx === page ? 'bg-[#006F53] text-white border-[#006F53]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  {idx + 1}
                </button>
              ))}
            <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages - 1}
              className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">›</button>
            <button onClick={() => setPage(totalPages - 1)} disabled={page === totalPages - 1}
              className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">»</button>
          </div>
        </div>
      )}
    </div>
  )
}

DataTable.propTypes = {
  rows:     PropTypes.array.isRequired,
  onEdit:   PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

// ─── Export logic ─────────────────────────────────────────────────────────────

// Maps row object to ordered array A–AH (34 values, indices 0–33)
// Template columns: A(0)…AG(32) + AH(33) [empty col in original]
function rowToArray(row) {
  const n = (v) => (v !== '' && v != null ? Number(v) : null)
  return [
    row.colA,    // A  (0)  ORGANISME
    row.colB,    // B  (1)  TIPUS DE CONTRACTE
    row.colC,    // C  (2)  ANY D'EXECUCIÓ
    row.colD,    // D  (3)  EXPEDIENT
    row.colE,    // E  (4)  NÚMERO DE LOT
    row.colF,    // F  (5)  DESCRIPCIÓ DEL CONTRACTE
    row.colG,    // G  (6)  TIPUS DE TRAMITACIÓ
    n(row.colH), // H  (7)  PRESSUPOST BASE LICITACIÓ SENSE IVA
    n(row.colI), // I  (8)  PRESSUPOST BASE LICITACIÓ AMB IVA
    n(row.colJ), // J  (9)  VALOR ESTIMAT
    row.colK,    // K  (10) TIPUS DE SUBMINISTRAMENTS
    row.colL,    // L  (11) ADJUDICATARI: NIF
    row.colM,    // M  (12) ADJUDICATARI: NOM
    row.colN,    // N  (13) ADJUDICATARI: PAÍS
    row.colO,    // O  (14) ADJUDICATARI: MUNICIPI
    row.colP,    // P  (15) ADJUDICATARI: PIME
    row.colQ,    // Q  (16) ADJUDICATARI: CETIS/EI
    row.colR,    // R  (17) RESERVA SOCIAL
    row.colS,    // S  (18) CODI CPV
    row.colT,    // T  (19) DATA ADJUDICACIÓ
    row.colU,    // U  (20) DATA FORMALITZACIÓ
    row.colV,    // V  (21) DATA INICI EXECUCIÓ
    row.colW,    // W  (22) DATA FI EXECUCIÓ
    row.colX,    // X  (23) TERMINI: ANYS
    row.colY,    // Y  (24) TERMINI: MESOS
    row.colZ,    // Z  (25) TERMINI: DIES
    row.colAA,   // AA (26) IMPORT D'ADJUDICACIÓ SENSE IVA
    row.colAB,   // AB (27) IMPORT ADJUDICACIÓ AMB IVA
    row.colAC,   // AC (28) TIPUS IVA
    row.colAD,   // AD (29) LLOC EXECUCIÓ
    row.colAE,   // AE (30) PAÍS ORIGEN DEL PRODUCTE
    row.colAF,   // AF (31) DATA LIQUIDACIÓ
    row.colAG,   // AG (32) IMPORT LIQUIDACIÓ (SENSE IVA)
    null,        // AH (33) [columna buida de la plantilla original]
  ]
}

async function exportToTemplate(rows, filename = 'Plantilla_agregades_menors_omplerta.xls') {
  // El backend llegeix la plantilla original del disc, omple els valors
  // i retorna el mateix fitxer XLS intacte
  const res = await fetch(`${API}/contractes/fill-template`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(rows.map(rowToArray)),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || 'Error generant la plantilla')
  }

  const blob = await res.blob()
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Desar a l'històric ─────────────────────────────────────────────────────

// 'DD/MM/AAAA' (o Date) → 'AAAA-MM-DD' per a columnes DATE de MySQL. Buit → null.
function toSqlDate(v) {
  if (v == null || v === '') return null
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  const m = String(v).match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (m) return `${m[3]}-${m[2]}-${m[1]}`
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10)
}

const numOrNull = (v) => (v !== '' && v != null && isFinite(Number(v)) ? Number(v) : null)

// Objecte de fila (col*) → registre de `contractes_menors` per persistir.
function colRowToDb(row) {
  return {
    tipus_import: 'MENOR',
    organisme:               row.colA || null,
    tipus_desti:             row.colB || null,
    any_execucio:            numOrNull(row.colC),
    expedient:               row.colD || null,
    num_lot:                 numOrNull(row.colE),
    descripcio:              row.colF || null,
    tipus_tramitacio:        row.colG || null,
    pressupost_licitacio_sense_iva: numOrNull(row.colH),
    pressupost_licitacio_amb_iva:   numOrNull(row.colI),
    valor_estimat:           numOrNull(row.colJ),
    tipus_subministraments:  row.colK || null,
    nif_tercer:              row.colL || null,
    nom_tercer:              row.colM || null,
    pais:                    row.colN || null,
    municipi:                row.colO || null,        // codi INE (5 dígits)
    // Origen del codi INE, per poder auditar d'on surt cada municipi resolt.
    cp_tercer:               row._cp || null,
    poblacio_tercer:         row._poblacio || null,
    pime:                    row.colP || null,
    cetis_ei:                row.colQ || null,
    reserva_social:          row.colR || null,
    codi_cpv:                row.colS || null,
    data_adjudicacio:        toSqlDate(row.colT),
    data_formalitzacio:      toSqlDate(row.colU),
    data_inici_execucio:     toSqlDate(row.colV),
    data_fi_execucio:        toSqlDate(row.colW),
    termini_anys:            numOrNull(row.colX),
    termini_mesos:           numOrNull(row.colY),
    termini_dies:            numOrNull(row.colZ),
    import_sense_iva:        numOrNull(row.colAA),
    import_amb_iva:          numOrNull(row.colAB),
    tipus_iva:               numOrNull(row.colAC),
    lloc_execucio:           row.colAD || null,
    pais_origen_producte:    row.colAE || null,
    data_liquidacio:         toSqlDate(row.colAF),
    import_liquidacio_sense_iva: numOrNull(row.colAG),
    import_total:            numOrNull(row.colAB),
    revisat: 0,
    exportat: 0,
  }
}

// Camps de la BD que l'editor controla (la resta es preserva de l'original).
const EDITOR_DB_FIELDS = [
  'organisme', 'tipus_desti', 'any_execucio', 'expedient', 'num_lot', 'descripcio',
  'tipus_tramitacio', 'pressupost_licitacio_sense_iva', 'pressupost_licitacio_amb_iva',
  'valor_estimat', 'tipus_subministraments', 'nif_tercer', 'nom_tercer', 'pais', 'municipi',
  'pime', 'cetis_ei', 'reserva_social', 'codi_cpv', 'data_adjudicacio', 'data_formalitzacio',
  'data_inici_execucio', 'data_fi_execucio', 'termini_anys', 'termini_mesos', 'termini_dies',
  'import_sense_iva', 'import_amb_iva', 'tipus_iva', 'lloc_execucio', 'pais_origen_producte',
  'data_liquidacio', 'import_liquidacio_sense_iva',
]

// ISO/Date → 'DD/MM/AAAA' per mostrar a l'editor.
const isoToDMY = (v) => {
  if (v == null || v === '') return ''
  const d = new Date(v)
  if (isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}
const numOrEmpty = (v) => (v == null || v === '' ? '' : Number(v))

// Registre de `contractes_menors` (BD) → objecte de fila (col*) per a l'editor.
// Guarda l'original a _db per fusionar-lo en desar (sense pèrdua de camps).
function dbRowToCol(db, idx) {
  return {
    _id: idx, _db: db,
    _cp: db.cp_tercer ?? '', _poblacio: db.poblacio_tercer ?? '',
    colA:  db.organisme ?? '',            colB:  db.tipus_desti ?? '',
    colC:  db.any_execucio ?? '',         colD:  db.expedient ?? '',
    colE:  numOrEmpty(db.num_lot),        colF:  db.descripcio ?? '',
    colG:  db.tipus_tramitacio ?? '',
    colH:  numOrEmpty(db.pressupost_licitacio_sense_iva),
    colI:  numOrEmpty(db.pressupost_licitacio_amb_iva),
    colJ:  numOrEmpty(db.valor_estimat),  colK:  db.tipus_subministraments ?? '',
    colL:  db.nif_tercer ?? '',           colM:  db.nom_tercer ?? '',
    colN:  db.pais ?? '',                 colO:  db.municipi ?? '',
    colP:  db.pime ?? '',                 colQ:  db.cetis_ei ?? '',
    colR:  db.reserva_social ?? '',       colS:  db.codi_cpv ?? '',
    colT:  isoToDMY(db.data_adjudicacio), colU:  isoToDMY(db.data_formalitzacio),
    colV:  isoToDMY(db.data_inici_execucio), colW: isoToDMY(db.data_fi_execucio),
    colX:  numOrEmpty(db.termini_anys),   colY:  numOrEmpty(db.termini_mesos),
    colZ:  numOrEmpty(db.termini_dies),   colAA: numOrEmpty(db.import_sense_iva),
    colAB: numOrEmpty(db.import_amb_iva), colAC: numOrEmpty(db.tipus_iva),
    colAD: db.lloc_execucio ?? '',        colAE: db.pais_origen_producte ?? '',
    colAF: isoToDMY(db.data_liquidacio),  colAG: numOrEmpty(db.import_liquidacio_sense_iva),
  }
}

// Fila de l'editor → registre BD per desar. Si prové d'una tanda existent (_db),
// fusiona només els camps editats sobre l'original (preserva text_lliure, etc.).
function rowToDbForSave(r) {
  const mapped = colRowToDb(r)
  if (!r._db) return mapped
  const merged = { ...r._db }
  EDITOR_DB_FIELDS.forEach((f) => { merged[f] = mapped[f] })
  delete merged.id
  delete merged.upload_id
  return merged
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ContractesPage() {
  const [rows, setRows]           = useState([])
  const [fileName, setFileName]   = useState(null)
  const [editRow, setEditRow]     = useState(null)
  const [exporting, setExporting] = useState(false)
  const [saving, setSaving]       = useState(false)
  const [trimestre, setTrimestre] = useState(Math.floor(new Date().getMonth() / 3) + 1)
  const [uploadId, setUploadId]   = useState(null)
  const [uploadEstat, setUploadEstat] = useState(null)
  const [dropdownOpts, setDropdownOpts] = useState({})

  const anyExec = () => Number(rows[0]?.colC) || new Date().getFullYear()
  const exportFileName = () => `agregades_menors_${anyExec()}_T${trimestre}.xls`

  const [, editParams] = useRoute('/contractes/editar/:id')
  const editId = editParams?.id

  useEffect(() => {
    fetch(`${API}/contractes/dropdown-options`)
      .then(r => r.json())
      .then(data => setDropdownOpts(data))
      .catch(() => {})
  }, [])


  // Carrega una tanda existent a l'editor (des de l'Històric).
  useEffect(() => {
    if (!editId) return
    let cancel = false
    ;(async () => {
      try {
        const res = await fetch(`${API}/contractes/uploads/${editId}`)
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Error carregant la tanda')
        if (cancel) return
        setRows((data.contractes || []).map((db, i) => dbRowToCol(db, i)))
        setFileName(data.upload?.nom_fitxer || `tanda_${editId}`)
        setUploadId(Number(editId))
        setUploadEstat(data.upload?.estat || 'pendent')
        if (data.upload?.trimestre) setTrimestre(data.upload.trimestre)
        toast.info(`Tanda #${editId} carregada a l'editor · pots editar-la i tornar-la a desar`)
      } catch (err) {
        if (!cancel) toast.error(err.message)
      }
    })()
    return () => { cancel = true }
  }, [editId])

  async function handleParsed(newRows, name) {
    setRows(newRows)
    setFileName(name)
    setUploadId(null)
    setUploadEstat(null)
    // Desa automàticament com a esborrany (pendent) en importar.
    try {
      const any = Number(newRows[0]?.colC) || new Date().getFullYear()
      const res = await fetch(`${API}/contractes/uploads`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          nom_fitxer:   name || `tanda_${any}_T${trimestre}`,
          trimestre:    Number(trimestre),
          any_execucio: any,
          contractes:   newRows.map(rowToDbForSave),
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Error desant esborrany')
      setUploadId(data.id)
      setUploadEstat('pendent')
      toast.info(`Esborrany desat automàticament (tanda #${data.id})`)
    } catch (err) {
      toast.warn('No s\'ha pogut desar l\'esborrany automàticament: ' + err.message)
    }
  }

  function handleEdit(row) {
    setEditRow(row)
  }

  function handleSave(updatedRow) {
    setRows(prev => prev.map(r => r._id === updatedRow._id ? updatedRow : r))
  }

  function handleDelete(id) {
    if (!window.confirm('Eliminar aquesta fila?')) return
    setRows(prev => prev.filter(r => r._id !== id))
  }

  function handleAddRow() {
    const newId = rows.length > 0 ? Math.max(...rows.map(r => r._id)) + 1 : 0
    const emptyRow = buildEmptyRow(newId)
    setRows(prev => [...prev, emptyRow])
    setEditRow(emptyRow)
  }

  function handleReset() {
    if (rows.length > 0 && !window.confirm('Descarta tots els canvis i torna a pujar un nou fitxer?')) return
    setRows([])
    setFileName(null)
    setUploadId(null)
    setUploadEstat(null)
  }

  async function handleExport() {
    if (rows.length === 0) return toast.warn('No hi ha dades per exportar')
    setExporting(true)
    const fname = exportFileName()
    try {
      await exportToTemplate(rows, fname)
      // En descarregar, la tanda passa a estat "exportat".
      if (uploadId) {
        await fetch(`${API}/contractes/uploads/${uploadId}`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ estat: 'exportat' }),
        })
        setUploadEstat('exportat')
      }
      toast.success(`Descarregat "${fname}" · ${rows.length} files` + (uploadId ? ' · tanda marcada com a exportada' : ''))
    } catch (err) {
      toast.error('Error exportant: ' + err.message)
    } finally {
      setExporting(false)
    }
  }

  // Desa els canvis de l'editor a la tanda actual (reemplaça les files).
  async function handleSaveUpload() {
    if (rows.length === 0) return toast.warn('No hi ha dades per desar')
    setSaving(true)
    try {
      const payload = {
        nom_fitxer:   fileName || `tanda_${anyExec()}_T${trimestre}`,
        trimestre:    Number(trimestre),
        any_execucio: anyExec(),
        contractes:   rows.map(rowToDbForSave),
      }
      let data
      if (uploadId) {
        const res = await fetch(`${API}/contractes/uploads/${uploadId}/contractes`, {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        })
        data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Error desant')
        toast.success(`Canvis desats a la tanda #${uploadId} (${data.total_menors} contractes)`)
      } else {
        const res = await fetch(`${API}/contractes/uploads`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        })
        data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Error desant')
        setUploadId(data.id)
        setUploadEstat('pendent')
        toast.success(`Tanda desada a l'històric (#${data.id}, ${data.total_menors} contractes)`)
      }
    } catch (err) {
      toast.error('Error desant a l\'històric: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const totalOk = rows.filter(r => !isIncomplete(r)).length

  return (
    <section className="bg-white min-h-full">
      <div className="px-6 py-8">

        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
            <LuClipboardList className="text-[#006F53]" size={26} />
            Agregades Menors — Plantilla
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Importa l&apos;Excel de SicalWin, revisa i edita les dades, i descarrega la plantilla
            {' '}<strong>Plantilla_agregades_menors.xls</strong> ja omplerta.
          </p>
          {editId && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-1.5">
              <LuHistory size={14} />
              Editant la tanda <strong>#{editId}</strong> de l&apos;històric. Els canvis es desaran a la mateixa tanda.
            </div>
          )}
        </div>

        {/* Step 1 — Upload */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              1. Importar fitxer SicalWin
            </h2>
            {fileName && (
              <button onClick={handleReset}
                className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1">
                <LuX size={12} /> Nou fitxer
              </button>
            )}
          </div>

          {fileName ? (
            <div className="flex items-center gap-3 p-4 bg-[#f0faf6] border border-[#c8e6da] rounded-xl">
              <LuFileSpreadsheet size={22} className="text-[#006F53] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {rows.length} files importades · {totalOk} correctes
                  {uploadId && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full font-medium ${
                      uploadEstat === 'exportat' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      tanda #{uploadId} · {uploadEstat === 'exportat' ? 'exportat' : 'esborrany'}
                    </span>
                  )}
                </p>
              </div>
              <button onClick={handleReset}
                className="text-xs text-gray-400 hover:text-red-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-red-200 hover:bg-red-50 transition-colors">
                Canviar fitxer
              </button>
            </div>
          ) : (
            <UploadZone onParsed={handleParsed} />
          )}
        </div>

        {/* Step 2 — Review & edit */}
        {rows.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                2. Revisar i editar les dades
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{rows.length} files</span>
                <button onClick={handleAddRow}
                  className="text-xs border border-[#006F53] text-[#006F53] rounded-lg px-3 py-1.5 hover:bg-[#f0faf6] transition-colors flex items-center gap-1">
                  <LuUpload size={12} /> Afegir fila manual
                </button>
              </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Total files',    val: rows.length },
                { label: 'Correctes',      val: totalOk,                  ok: true },
                { label: 'Amb advertències', val: rows.length - totalOk, warn: rows.length - totalOk > 0 },
                { label: 'Import total (s/IVA)',
                  val: fmtEur(rows.reduce((s, r) => s + (typeof r.colH === 'number' ? r.colH : 0), 0)) },
              ].map(s => (
                <div key={s.label}
                  className={`rounded-lg p-3 border ${
                    s.warn ? 'border-yellow-300 bg-yellow-50' : 'border-[#c8e6da] bg-[#f0faf6]'
                  }`}>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className={`text-lg font-bold ${s.warn ? 'text-yellow-700' : 'text-[#006F53]'}`}>{s.val}</p>
                </div>
              ))}
            </div>

            <DataTable rows={rows} onEdit={handleEdit} onDelete={handleDelete} />
          </div>
        )}

        {/* Step 3 — Export */}
        {rows.length > 0 && (
          <div className="border-t border-[#e0f0e8] pt-6">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
              3. Descarregar plantilla omplerta
            </h2>
            <div className="flex flex-wrap items-center gap-4 p-5 bg-[#f0faf6] border border-[#c8e6da] rounded-xl">
              <LuFileSpreadsheet size={30} className="text-[#006F53] flex-shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-xs text-gray-500">Es descarregarà el fitxer:</p>
                <p className="text-sm font-semibold text-gray-800 break-all">{exportFileName()}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Plantilla oficial d&apos;agregades menors · {rows.length} files (a partir de la fila 2) · trimestre {trimestre} · {anyExec()}
                </p>
                {rows.length - totalOk > 0 && (
                  <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                    <LuCircleAlert size={12} />
                    {rows.length - totalOk} fila{rows.length - totalOk > 1 ? 'es' : ''} amb camps buits — revisa-les abans de descarregar.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Trimestre</label>
                <select
                  value={trimestre}
                  onChange={e => setTrimestre(Number(e.target.value))}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#006F53]/30">
                  <option value={1}>1r</option>
                  <option value={2}>2n</option>
                  <option value={3}>3r</option>
                  <option value={4}>4t</option>
                </select>
                <button
                  onClick={handleSaveUpload}
                  disabled={saving}
                  title="Desa els canvis a la tanda de l'històric"
                  className="flex items-center gap-2 px-4 py-2.5 border border-[#006F53] text-[#006F53] text-sm rounded-lg hover:bg-[#eef8f3] disabled:opacity-60 transition-colors whitespace-nowrap">
                  <LuHistory size={16} />
                  {saving ? 'Desant...' : 'Desar canvis'}
                </button>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  title={`Descarrega ${exportFileName()} i marca la tanda com a exportada`}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#006F53] text-white text-sm rounded-lg hover:bg-[#005540] disabled:opacity-60 transition-colors whitespace-nowrap">
                  <LuDownload size={16} />
                  {exporting ? 'Generant...' : 'Descarregar Excel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {rows.length === 0 && !fileName && (
          <div className="mt-2 text-center py-8">
            <p className="text-xs text-gray-400">
              Columnes llegides del fitxer SicalWin: A (import), C (exercici), D (NIF),
              E (nom proveïdor), F (text lliure: tipus, descripció, CPV, expedient), G (data doc.),
              H (població), I (tipus contracte). La B (expedient) només es fa servir si el text lliure no el porta.
            </p>
          </div>
        )}

      </div>

      {/* Edit modal */}
      {editRow && (
        <EditModal
          row={editRow}
          opts={dropdownOpts}
          onSave={handleSave}
          onClose={() => setEditRow(null)}
        />
      )}
    </section>
  )
}
